import {
  NonQuotaTargeting,
  QUALIFICATION_CODE,
  QuotaCriteria,
} from '@ps/survey-utils';
import { nonEmptyList } from '@ps/utils';

import {
  FeasibilityCriteriaDto,
  FeasibilityDefaultQuotaCriteriaDto,
} from '../dtos/feasibility.dto';

import { rangeSetsFor } from '../domain';

/** One quota row's `criteria` array, in wire shape. */
export class FeasibilityQuotaCriteriaPayloadMapper {
  constructor(
    private readonly _defaultCriteria: readonly FeasibilityDefaultQuotaCriteriaDto[],
    private readonly _targeting: NonQuotaTargeting,
  ) {}

  /** Each row carries the whole default bucket overlaid by its own criteria, so the
   *  API sees the survey's full targeting context per quota (legacy behaviour). */
  public mapForQuota(
    ownCriteria: readonly QuotaCriteria[],
  ): FeasibilityCriteriaDto[] {
    return this._overlaidOnDefaults(
      ownCriteria.map((criterion) => this._toCriterionDto(criterion)),
    ).map((criterion) => this._withWireRules(criterion));
  }

  /** No quota targets anything: the synthesised row targets the defaults verbatim. */
  public mapForDefaultQuota(): FeasibilityCriteriaDto[] {
    return this._defaultCriteria.map((criterion) =>
      this._withWireRules(criterion),
    );
  }

  /** Defaults keyed first: a quota's own criterion replaces in place, a new one appends. */
  private _overlaidOnDefaults(
    ownCriteria: readonly FeasibilityCriteriaDto[],
  ): FeasibilityCriteriaDto[] {
    const byQualification = new Map<number, FeasibilityCriteriaDto>(
      this._defaultCriteria.map((criterion) => [
        criterion.qualification_code,
        criterion,
      ]),
    );
    ownCriteria.forEach((criterion) =>
      byQualification.set(criterion.qualification_code, criterion),
    );

    return [...byQualification.values()];
  }

  private _withWireRules(
    criterion: FeasibilityCriteriaDto,
  ): FeasibilityCriteriaDto {
    return this._withoutUploadedNames({
      ...criterion,
      non_quota_targeted: this._targeting.flagForQuotaRow(
        criterion.qualification_code,
      ),
    });
  }

  /** ZIP uploads run to tens of thousands and the codes identify them. Emptied, not
   *  removed — an absent key would read as "no conditions at all". */
  private _withoutUploadedNames(
    criterion: FeasibilityCriteriaDto,
  ): FeasibilityCriteriaDto {
    if (criterion.qualification_code !== QUALIFICATION_CODE.ZIPCODE) {
      return criterion;
    }

    return nonEmptyList<string | Record<string, string>>(
      criterion.condition_names,
    ).mapOr(criterion, () => ({ ...criterion, condition_names: [] }));
  }

  private _toCriterionDto(criterion: QuotaCriteria): FeasibilityCriteriaDto {
    return {
      qualification_code: criterion.qualificationCode,
      qualification_name: criterion.qualificationName,
      ...nonEmptyList(criterion.conditionCodes).mapOr({}, (codes) => ({
        condition_codes: [...codes],
      })),
      ...nonEmptyList(criterion.conditionNames).mapOr({}, (names) => ({
        condition_names: [...names],
      })),
      ...rangeSetsFor(criterion).mapOr({}, (ranges) => ({
        range_sets: ranges,
      })),
    };
  }
}
